<template>
  <el-select
    v-model="value"
    :class="{ desktop: isDesktop }"
    :multiple="multiple"
    :disabled="disabled"
    filterable
    remote
    reserve-keyword
    placeholder="Enter 2 or more characters"
    no-data-text="Could not find and spots matching your query"
    no-match-text="Could not find any spots for your query"
    loading-text="Loading..."
    :remote-method="lookup"
    :loading="loading"
    @update:modelValue="update"
  >
    <el-option
      v-for="item in options"
      :key="item.id"
      :label="item.text"
      :value="item.id"
    />
  </el-select>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  onMounted,
  watchEffect,
  watch
} from 'vue'
import { ISpotsPayload, ISpot } from '@/apps/traforama/api/spots'
import { useSpotsStore } from '@/apps/traforama/store/spots'
import useResize from '@/hooks/useResize'

interface Option {
  id: string
  text: string
}

export default defineComponent({
  name: 'SpotsSelect',
  props: {
    modelValue: {
      required: true,
      type: [String, Array]
    },
    items: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const value = ref(props.modelValue)
    const spotsStore = useSpotsStore()
    const loading = ref(false)
    const options = reactive<Array<Option>>([])

    const loadInitial = async () => {
      if (props.modelValue.length < 1) {
        return
      }
      const payload = {
        filter: {
          id: props.modelValue as Array<number>
        },
        limit: props.modelValue.length,
        start: 0
      } as ISpotsPayload
      const response = await spotsStore.SearchItems(payload)
      assignOptions(response)
    }
    loadInitial()

    watch(
      (_: any) => props.modelValue,
      (_) => {
        if (props.modelValue.length < 1) {
          assignOptions([])
        }
      }
    )

    const assignOptions = (items: Array<ISpot>) => {
      const newOptions = items.map((item: ISpot) => {
        return { id: item.id, text: item.attributes.name } as Option
      })
      options.splice(0, options.length, ...newOptions)
    }

    const lookup = async (query: string) => {
      if (query.length >= 2) {
        loading.value = true
        const payload = {
          filter: {
            name__or__id: query
          },
          limit: 20,
          start: 0
        } as ISpotsPayload
        const response = await spotsStore.SearchItems(payload)
        assignOptions(response)
        loading.value = false
      } else {
        options.splice(0, options.length, ...[])
      }
    }

    const update = (newValue: any) => {
      context.emit('update:modelValue', newValue)
    }

    const { isDesktop } = useResize()

    return { loading, lookup, options, update, isDesktop, value }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}
</style>
